/**
 * Lazy-loaded icons with encapsulated Suspense boundaries.
 */
import React, { Suspense } from 'react';

// oxlint-disable-next-line @atlassian/no-restricted-imports
import { lazyForPaint } from '@atlassian/react-loosely-lazy';

import { withBrandIcon, type BrandIcon } from '../../../icons/with-brand-icon';

const OfficialIcon = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "@atlassian/logo-third-party/google-calendar" */ '@atlassian/logo-third-party/google-calendar'
		).then((m) => m.GoogleCalendarIcon),
	{ moduleId: '@atlassian/logo-third-party/google-calendar' },
);

const LazyGoogleCalendarIconComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-google-calendar" */ './utils').then(
			(m) => m.GoogleCalendarIcon,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/google-calendar/icon' },
);

const LazyGoogleCalendarSearchResultIconComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-google-calendar-search-result" */ './utils').then(
			(m) => m.GoogleCalendarSearchResultIcon,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/google-calendar/search-result-icon' },
);

const LazyGoogleCalendarIconBorderlessComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-google-calendar-borderless" */ './utils').then(
			(m) => m.GoogleCalendarIconBorderless,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/google-calendar/icon-borderless' },
);

/**
 * Exported icons with self-contained Suspense boundaries.
 * Consumers can use these directly without wrapping in LazySuspense.
 */
export const GoogleCalendarIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyGoogleCalendarIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'small',
);

export const GoogleCalendarSearchResultIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyGoogleCalendarSearchResultIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'medium',
);

export const GoogleCalendarIconBorderless: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyGoogleCalendarIconBorderlessComponent />
		</Suspense>
	),
	OfficialIcon,
	'xxsmall',
);
